import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import {
  JwtCheckInModel,
  OrderMode,
  TerminalModel,
  UpdateTerminalRequest,
} from '@models/terminal/terminal.model';
import { ResponseModel } from '@models/response.model';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { EnterpriseService } from './enterprise.service';
import { HttpService } from './http.service';

const LIST_STORAGE_KEY = 'terminals_list_cache';
const LIST_STORAGE_KEY_TS = 'terminals_list_cache_ts';
const SINGLE_STORAGE_KEY_PREFIX = 'terminal_';
const SINGLE_STORAGE_KEY_TS_PREFIX = 'terminal_ts_';
const SELECTED_TERMINAL_ID_KEY = 'selected_terminal_id';
const CACHE_AGE = 60 * 1000 * 5; // 5 minutes

@Injectable({
  providedIn: 'root',
})
export class TerminalService {
  private readonly terminalsApi = `${environment.caisseApiUrl}/self-pos-terminals`;
  private readonly httpService = inject(HttpService);
  private readonly enterpriseService = inject(EnterpriseService);

  private readonly selectedTerminalId = signal<number | null>(this.#readSelectedId());
  private readonly terminals = signal<TerminalModel[]>(this.#readListStorage());
  private readonly terminalListLastUpdate = signal<number>(
    Number(localStorage.getItem(LIST_STORAGE_KEY_TS) ?? 0),
  );
  private readonly terminalSingleLastUpdate = signal<number>(
    Number(localStorage.getItem(SINGLE_STORAGE_KEY_TS_PREFIX + this.selectedTerminalId()) ?? 0),
  );

  readonly currentTerminal = signal<TerminalModel | null>(
    this.#readListStorage().find((t) => t.id === this.selectedTerminalId()) ??
      this.#readListStorage()[0] ??
      null,
  );

  readonly orderModes = computed<OrderMode[]>(() => {
    const terminal = this.currentTerminal();
    const modes: OrderMode[] = [];
    if (terminal?.is_meal_plan_enabled !== false) {
      modes.push('meal_plan');
    }
    if (terminal?.is_by_course_enabled !== false) {
      modes.push('by_course');
    }
    return modes;
  });

  readonly hasSelectedInstance = computed(() => this.selectedTerminalId() !== null);

  private readonly tenantMismatchDetected = signal(false);
  readonly hasTenantMismatch = computed(() => this.tenantMismatchDetected());

  markTenantMismatch(): void {
    this.tenantMismatchDetected.set(true);
  }

  clearTenantMismatch(): void {
    this.tenantMismatchDetected.set(false);
  }

  readonly currentInstanceLabel = computed(() => this.getInstanceLabel(this.currentTerminal()));

  getInstanceLabel(terminal: TerminalModel | null): string {
    const enterpriseName = this.enterpriseService.currentEnterprise()?.name;
    return [enterpriseName, terminal?.label].filter(Boolean).join(' — ');
  }

  private listInFlight$: Observable<TerminalModel[]> | null = null;
  private singleInFlight$: Map<number, Observable<TerminalModel>> = new Map();

  getTerminals(): Observable<TerminalModel[]> {
    if (this.terminalListLastUpdate() > Date.now() - CACHE_AGE) {
      return of(this.terminals());
    }
    return this.loadTerminals();
  }

  loadTerminals(): Observable<TerminalModel[]> {
    if (!this.listInFlight$) {
      this.listInFlight$ = this.httpService.get<TerminalModel[]>(this.terminalsApi).pipe(
        tap((data) => {
          this.terminals.set(data);
          this.currentTerminal.set(
            data.find((t) => t.id === this.selectedTerminalId()) ?? data[0] ?? null,
          );
          this.listInFlight$ = null;
          localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify(data));
          localStorage.setItem(LIST_STORAGE_KEY_TS, String(Date.now()));
        }),
        shareReplay(1),
      );
    }
    return this.listInFlight$;
  }

  getTerminalById(id: number): Observable<TerminalModel> {
    if (this.terminalSingleLastUpdate() > Date.now() - CACHE_AGE) {
      const cachedList = this.terminals();
      const terminalInList = cachedList.find((t) => t.id === id);
      if (terminalInList) {
        return of(terminalInList);
      }

      const cachedTerminal = this.#readSingleStorage(id);
      const lastUpdate = Number(localStorage.getItem(`${SINGLE_STORAGE_KEY_TS_PREFIX}${id}`) ?? 0);
      if (cachedTerminal && lastUpdate > Date.now() - CACHE_AGE) {
        return of(cachedTerminal);
      }
    }
    return this.loadTerminalById(id);
  }

  loadTerminalById(id: number): Observable<TerminalModel> {
    if (!this.singleInFlight$.has(id)) {
      const request$ = this.httpService.get<TerminalModel>(`${this.terminalsApi}/${id}`).pipe(
        tap((data) => {
          this.#applyUpdatedTerminal(data);
          this.singleInFlight$.delete(id);
        }),
        shareReplay(1),
      );
      this.singleInFlight$.set(id, request$);
    }
    return this.singleInFlight$.get(id)!;
  }

  checkInToken(id: number): Observable<JwtCheckInModel> {
    return this.httpService.post<JwtCheckInModel>(`${this.terminalsApi}/${id}/check-in-token`);
  }

  updateTerminal(request: UpdateTerminalRequest): Observable<ResponseModel> {
    return this.httpService
      .put<ResponseModel, UpdateTerminalRequest>(this.terminalsApi, request)
      .pipe(tap(() => this.#applyUpdatedTerminal(request)));
  }

  selectTerminal(terminal: TerminalModel): void {
    this.selectedTerminalId.set(terminal.id);
    localStorage.setItem(SELECTED_TERMINAL_ID_KEY, String(terminal.id));
    this.currentTerminal.set(terminal);
  }

  #applyUpdatedTerminal(updated: TerminalModel): void {
    this.terminals.update((list) =>
      list.map((terminal) => (terminal.id === updated.id ? updated : terminal)),
    );
    if (this.currentTerminal()?.id === updated.id) {
      this.currentTerminal.set(updated);
    }
    localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify(this.terminals()));
    localStorage.setItem(`${SINGLE_STORAGE_KEY_PREFIX}${updated.id}`, JSON.stringify(updated));
    localStorage.setItem(`${SINGLE_STORAGE_KEY_TS_PREFIX}${updated.id}`, String(Date.now()));
  }

  #readSelectedId(): number | null {
    const raw = localStorage.getItem(SELECTED_TERMINAL_ID_KEY);
    return raw ? Number(raw) : null;
  }

  #readListStorage(): TerminalModel[] {
    try {
      const raw = localStorage.getItem(LIST_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as TerminalModel[]) : [];
    } catch {
      return [];
    }
  }

  #readSingleStorage(id: number): TerminalModel | null {
    try {
      const raw = localStorage.getItem(`${SINGLE_STORAGE_KEY_PREFIX}${id}`);
      return raw ? (JSON.parse(raw) as TerminalModel) : null;
    } catch {
      return null;
    }
  }
}
